import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../../utils/cn';
import { Button } from '../../../components/button';
import type { HeaderCartInfo } from './Header';
import {
    _headerVariants,
    type HeaderNavLinks,
    type HeaderUserData,
} from './Header';
import { MobileGlassMenu } from './MobileGlassMenu';
import { MobileUserMenu } from './user-menu/MobileUserMenu';
import { LogoAnimated } from '../../../components/logo-animated/LogoAnimated';
import { RoccoLink } from '../../../components/link/RoccoLink';
import { WarningBanner } from '../../../ui/warning-banner/components/WarningBanner';
import { UserCart } from './UserCart';
import { Badge } from '../../../components/badge';

/**
 * The header's outline glyphs, each drawn on a box the exact size it shows
 * at — one viewBox unit to one pixel — so the stroke lands at the weight it
 * is written at rather than scaling with the icon. A hairline 1px set at
 * 15–18px, the menu's two bars a touch heavier at 1.3: small and even beside
 * the wordmark.
 */
type GlyphProps = { className?: string };

/**
 * The left group's glyphs are the designer's own drawings: filled shapes on
 * 50-unit artboards, each cropped to its drawing and set at one shared scale
 * (0.575, which puts the menu at 18px wide) so they keep their sizes relative
 * to each other. They take the header's fill, so they go white over a hero.
 */
const MenuGlyph = ({ className }: GlyphProps) => (
    <svg
        viewBox="9.145 16.39 31.29 15.78"
        aria-hidden="true"
        className={cn('h-[9.08px] w-[18.00px] shrink-0', className)}
    >
        <path d="M40.435 16.39H9.145V18.39H40.435V16.39Z" />
        <path d="M40.435 30.17H9.145V32.17H40.435V30.17Z" />
    </svg>
);

const SearchGlyph = ({ className }: GlyphProps) => (
    <svg
        viewBox="11.458 9.981 27.765 29.989"
        aria-hidden="true"
        className={cn('h-[17.25px] w-[15.97px] shrink-0', className)}
    >
        <path d="M38.93 38.2678L31.11 30.4478C33.95 27.8678 35.54 23.9378 34.8 19.6678C33.97 14.8478 30.09 10.9778 25.28 10.1578C17.22 8.77782 10.3 15.6778 11.62 23.7278C12.41 28.5278 16.22 32.4078 20.99 33.2978C24.14 33.8878 27.11 33.1878 29.51 31.6678L37.52 39.6778C37.91 40.0678 38.54 40.0678 38.93 39.6778C39.32 39.2878 39.32 38.6578 38.93 38.2678ZM13.59 23.4078C12.49 16.7378 18.2 11.0278 24.87 12.1278C28.91 12.7978 32.17 16.0578 32.84 20.0978C33.94 26.7678 28.23 32.4878 21.56 31.3778C17.52 30.7078 14.26 27.4478 13.59 23.4078Z" />
    </svg>
);

const BellGlyph = ({ className }: GlyphProps) => (
    <svg
        viewBox="13.304 11.257 24.992 28.703"
        aria-hidden="true"
        className={cn('h-[16.51px] w-[14.38px] shrink-0', className)}
    >
        <path d="M38.11 32.58L37.24 30.9C36.62 29.69 36.29 28.34 36.29 26.98V22.07C36.29 16.38 31.99 11.64 26.5 11.28C23.56 11.09 20.77 12.09 18.64 14.09C16.53 16.06 15.32 18.85 15.32 21.74V26.98C15.32 28.34 14.99 29.69 14.37 30.9L13.5 32.58C13.22 33.13 13.24 33.77 13.56 34.3C13.88 34.83 14.44 35.14 15.06 35.14H21.13V35.29C21.13 37.87 23.23 39.96 25.8 39.96C28.37 39.96 30.47 37.86 30.47 35.29V35.14H36.54C37.16 35.14 37.72 34.83 38.04 34.3C38.36 33.77 38.38 33.13 38.1 32.58H38.11ZM25.81 37.96C24.38 37.96 23.21 36.83 23.14 35.42H28.48C28.41 36.83 27.24 37.96 25.81 37.96ZM15.47 33.14L16.15 31.81C16.92 30.32 17.32 28.65 17.32 26.98V21.74C17.32 19.37 18.27 17.17 20 15.55C21.73 13.93 23.99 13.12 26.37 13.28C30.81 13.57 34.29 17.43 34.29 22.07V26.98C34.29 28.65 34.69 30.32 35.46 31.81L36.14 33.14H15.46H15.47Z" />
    </svg>
);

const BookmarkGlyph = ({ className }: GlyphProps) => (
    <svg
        viewBox="0 0 16 16"
        aria-hidden="true"
        className={cn('size-[16px] shrink-0', className)}
    >
        <path
            d="M2.5 0.5h11v15L8 11.5l-5.5 4v-15Z"
            strokeWidth="1"
            strokeLinejoin="round"
        />
    </svg>
);

const AccountGlyph = ({ className }: GlyphProps) => (
    <svg
        viewBox="0 0 16 16"
        aria-hidden="true"
        className={cn('size-[16px] shrink-0', className)}
    >
        <circle cx="8" cy="4.5" r="4" strokeWidth="1" />
        <path
            d="M0.5 15.5c0-3.3 3.4-5.5 7.5-5.5s7.5 2.2 7.5 5.5"
            strokeWidth="1"
        />
    </svg>
);

const CartGlyph = ({ className }: GlyphProps) => (
    <svg
        viewBox="0 0 15 16"
        aria-hidden="true"
        className={cn('h-[16px] w-[15px] shrink-0', className)}
    >
        <path
            d="M0.5 4.5h14l-1 11h-12l-1-11ZM4.5 6.5V4a3 3 0 0 1 6 0v2.5"
            strokeWidth="1"
            strokeLinejoin="round"
        />
    </svg>
);

const _headerIconVariants = cva('z-fixed', {
    variants: {
        // Dark is the page ink, #323232 — the colour the page's own marks
        // (the directory's A, its headings) are set in — rather than a
        // second, harder black a few pixels above them.
        fill: {
            dark: 'fill-[#323232] text-[#323232]',
            light: 'fill-white text-white',
            none: 'fill-none',
        },
        outline: {
            default: '',
            light: 'fill-none stroke-white',
            dark: 'fill-none stroke-[#323232]',
        },
    },
    defaultVariants: {
        fill: 'dark',
        outline: 'default',
    },
});

interface MobileHeaderProps extends VariantProps<typeof _headerVariants> {
    /** The solid header's fill on a page that is not white. */
    groundClassName?: string;
    /** Scrolled-solid theme: dark editorial pages go near-black with light icons. */
    solidTheme?: 'light' | 'dark';
    primaryLinks: HeaderNavLinks;
    secondaryLinks: HeaderNavLinks;
    socialLinks?: HeaderNavLinks;
    profileLinks: HeaderNavLinks;
    userData: HeaderUserData;
    onLogout: () => void;
    onLoginClick: () => void;
    currentUrl: string;
    onUserCartClick: () => void;
    /**
     * Open the mini-cart on hover. Off where the cart icon opens a drawer
     * instead; defaults to the hover the legacy header has always had.
     */
    openCartMenuOnHover?: boolean;
    warningMessages?: string[];
    isLegacyBrokenRemMode?: boolean;
    onMobileSearchClick?: () => void;
    cartInfo: HeaderCartInfo;
    currentCountryCode?: string;
    /**
     * Small text rendered above the primary links in the drawer when set
     * (hero-overlay / B2B routes). Visual integration TBD; accepting the prop
     * now so the Header call site type-checks.
     */
    tagline?: string;
}

export const MobileHeader = (props: MobileHeaderProps) => {
    const {
        primaryLinks,
        secondaryLinks,
        socialLinks,
        profileLinks,
        color,
        userData,
        onLogout,
        currentUrl,
        mode,
        onUserCartClick,
        openCartMenuOnHover,
        warningMessages,
        isLegacyBrokenRemMode,
        onMobileSearchClick,
        cartInfo,
        currentCountryCode,
        onLoginClick,
        solidTheme = 'light',
    } = props;

    const baseMenuColor = color === 'transparent' ? 'light' : 'dark';

    // The transparent hero header is `fixed`, so as soon as the page scrolls the
    // bar would otherwise sit over light page content. Switch it to a solid white
    // bar (with dark icons) shortly after scroll begins.
    const [scrolledPastHero, setScrolledPastHero] = useState(false);
    useEffect(() => {
        if (color !== 'transparent') return;
        const onScroll = () => setScrolledPastHero(window.scrollY > 100);
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, [color]);

    const showSolid = color === 'transparent' && scrolledPastHero;
    const solidIsDark = solidTheme === 'dark';
    // The solid presentation appears two ways: this component's own
    // scrolled-past-hero switch, and the app flipping `color` to 'solid'
    // past the hero. Theme both — dark editorial pages keep light icons on
    // near-black instead of the default white bar.
    const effectiveSolid = showSolid || color === 'solid';
    const menuColor = effectiveSolid
        ? solidIsDark
            ? 'light'
            : 'dark'
        : baseMenuColor;
    const fillClass = _headerIconVariants({ fill: menuColor });
    const outlineClass = _headerIconVariants({ outline: menuColor });
    const basicTheme = cn(
        // No width guard here. `Header` picks this or `DesktopHeader` by
        // display mode and returns one of them, so the two are never both
        // mounted — and the mode now answers to how much room the reader has
        // rather than to viewport width alone. A `md:hidden` left this strip
        // invisible between 768px and the mode's own threshold, which is
        // exactly where a reader at 200% zoom lands: no header at all.
        'z-fixed',
        _headerVariants({ color, mode }),
        // Site-wide top ad banner: the fixed bar sits below the banner and
        // slides up to the top as the banner scrolls away. Falls back to 0px
        // when no banner is present, so layout is unchanged without it.
        'top-[var(--ad-top-banner-offset,0px)]',
        // The solid state overrides a `transparent` colour variant without
        // dropping its classes, so the light bar resets the focus-ring token
        // the variant flipped to off-white.
        effectiveSolid &&
            (solidIsDark
                ? 'bg-[#181818] text-white focus-ring-on-dark'
                : 'bg-white text-[#181818] focus-ring-on-light'),
        effectiveSolid && !solidIsDark && props.groundClassName,
    );

    const [isNavOpen, setNavOpen] = useState(false);
    const headerRef = useRef<HTMLDivElement>(null);

    const { cartCount } = cartInfo;

    useEffect(() => {
        setNavOpen(false);
    }, [currentUrl]);

    // Publish the live fixed-header height (nav + optional warning) so default-
    // theme page content can clear it. A static 74px pad only covers the bar.
    useLayoutEffect(() => {
        const header = headerRef.current;
        if (!header) return;

        const layout =
            header.closest('#app-page-layout') ??
            document.getElementById('app-page-layout');
        if (!(layout instanceof HTMLElement)) return;

        const apply = () => {
            layout.style.setProperty(
                '--mobile-header-height',
                `${header.getBoundingClientRect().height}px`,
            );
        };

        apply();
        const resizeObserver = new ResizeObserver(apply);
        resizeObserver.observe(header);

        return () => {
            resizeObserver.disconnect();
            layout.style.removeProperty('--mobile-header-height');
        };
    }, [warningMessages]);

    return (
        <div ref={headerRef} className={basicTheme} id="mobile-header">
            {/* Match desktop: the warning pins above the nav row. It lives
                inside the fixed header strip so it moves with it — a page-flow
                banner would be overlapped by the fixed header. */}
            {!!warningMessages?.length && (
                <WarningBanner alerts={warningMessages} />
            )}
            {/* Keep height in sync with drawer max height. */}
            {/* packages/rocco/src/components/drawer/drawer-content.tsx */}
            {/* Three groups on one line: the ways into the site on the
                left (menu, search, what is new), the wordmark centred on the
                bar, and the reader's own things on the right (saves, cart,
                account), with the account always outermost. */}
            <div className="relative flex h-19 items-center justify-between px-[24px] py-3">
                <div className="flex items-center gap-x-[24px]">
                    {/* The glyph's own place, kept in the row while the
                        glass card that grows from it is drawn over the
                        row; see MobileGlassMenu. */}
                    <div aria-hidden="true" className="-m-3 size-11 p-3" />
                    <MobileGlassMenu
                        open={isNavOpen}
                        onOpenChange={setNavOpen}
                        glyph={<MenuGlyph className={fillClass} />}
                        primaryLinks={primaryLinks}
                        secondaryLinks={secondaryLinks}
                        socialLinks={socialLinks}
                        isAuthorized={userData.isAuthorized}
                        currentUrl={currentUrl}
                        logout={onLogout}
                    />
                    {/* No `asChild` on the handler form: it makes Button a
                            Slot, which merges the props into its child and
                            renders that instead — so the control reaching the
                            page was a bare <svg> carrying an onClick, with no
                            button around it, no name and no tab stop. It could
                            be tapped and not reached any other way.

                            The icon is the whole label either way, so the name
                            lives on the control and the glyph is hidden from
                            the accessibility tree rather than read as a second
                            thing. */}
                    {onMobileSearchClick ? (
                        <Button
                            size="square24"
                            className="-m-3 size-auto border-0 p-3"
                            shape="square"
                            color="transparent"
                            overflow="visible"
                            aria-label="Search"
                            onClick={onMobileSearchClick}
                        >
                            <SearchGlyph className={fillClass} />
                        </Button>
                    ) : (
                        <Button
                            size="square24"
                            className="-m-3 size-auto border-0 p-3"
                            shape="square"
                            color="transparent"
                            overflow="visible"
                            asChild
                        >
                            <RoccoLink to="/search" aria-label="Search">
                                <SearchGlyph className={fillClass} />
                            </RoccoLink>
                        </Button>
                    )}
                    {/* Only when there is something new: an empty bell is a
                        control with nothing behind it, and the bar reads
                        cleaner without it. The count rides the bell rather
                        than the avatar, on the thing it counts. */}
                    {userData.isAuthorized &&
                    userData.notificationsCount > 0 ? (
                        <RoccoLink
                            to="/app/notifications"
                            aria-label={`Notifications (${userData.notificationsCount})`}
                            className="-m-3 flex items-center justify-center p-3"
                        >
                            {/* Block, not flex: the header's fill class
                                carries a z-index, and a flex item honours it
                                unpositioned — the bell then painted over its
                                own count. */}
                            <span className="relative block">
                                <BellGlyph className={cn(fillClass, 'block')} />
                                <span className="absolute -top-2.5 -right-2.5 flex">
                                    <Badge
                                        className="bg-[#323232]"
                                        size="centeredCompact"
                                        text={
                                            userData.notificationsCount > 9
                                                ? 'small'
                                                : 'default'
                                        }
                                    >
                                        {userData.notificationsCount > 9
                                            ? '9+'
                                            : userData.notificationsCount}
                                    </Badge>
                                </span>
                            </span>
                        </RoccoLink>
                    ) : null}
                </div>

                <RoccoLink
                    to="/"
                    className={cn(
                        'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
                        fillClass,
                    )}
                    aria-label="Home"
                >
                    {/* 80% of the 1.6 it was: the wordmark leads the page at
                        that size, and the page should. */}
                    <LogoAnimated
                        size={1.28}
                        mode="disabled"
                        color={menuColor}
                        isLegacyBrokenRemMode={isLegacyBrokenRemMode}
                        disableColorTransition
                        className={
                            menuColor === 'dark'
                                ? 'text-[#323232] hover:text-[#323232]'
                                : undefined
                        }
                    />
                </RoccoLink>

                <div className="flex items-center gap-x-[24px]">
                    {userData.isAuthorized ? (
                        <RoccoLink
                            to="/app/boards"
                            aria-label="Design boards"
                            className="-m-3 flex items-center justify-center p-3"
                        >
                            <BookmarkGlyph className={outlineClass} />
                        </RoccoLink>
                    ) : null}

                    {cartCount > 0 && (
                        <UserCart
                            onUserCartClick={onUserCartClick}
                            openCartMenuOnHover={openCartMenuOnHover}
                            // Ink in every theme: the glyph is the header's
                            // own, so fill only colours the count, and the
                            // count matches the bell's beside it rather than
                            // turning brick on a dark bar.
                            fill="ink"
                            cartInfo={cartInfo}
                            icon={<CartGlyph className={outlineClass} />}
                        />
                    )}
                    {userData.isAuthorized ? (
                        <div className="flex">
                            <MobileUserMenu
                                revamp
                                userData={userData}
                                profileLinks={profileLinks}
                                onLogout={onLogout}
                                currentUrl={currentUrl}
                                currentCountryCode={currentCountryCode}
                            />
                        </div>
                    ) : (
                        <div className="flex gap-x-3">
                            <Button
                                size="square24"
                                className="-m-3 size-auto border-0 p-3"
                                shape="square"
                                color="transparent"
                                overflow="visible"
                                asChild
                            >
                                <button
                                    aria-label="Log in"
                                    onClick={onLoginClick}
                                >
                                    <AccountGlyph className={outlineClass} />
                                </button>
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
